/**
 * Spec 160 (research D-160-08, contracts/adresse-client.md) — le titre de l'onglet et le libellé de la page.
 *
 * Le libellé d'une page vient de la **navigation déclarée** : l'entrée de `getNavStructure(role)` dont le chemin est le
 * plus long préfixe de la page. Les entrées des solutions y arrivent par le registre (`navSection.items[].label`), où
 * chaque solution les déclare déjà ; la plateforme n'en connaît aucune.
 *
 * ⚠️ Ne jamais ajouter ici de table « chemin → libellé » pour une page de solution : les contrôles de frontière C1 à C4
 * ne la verraient pas (contrainte G5 du garde-fou). La table ci-dessous ne nomme que des pages de la plateforme.
 *
 * Le libellé courant est aussi tenu dans une variable de module : l'initialiseur de télémétrie le lit pour nommer la
 * vue de page sans le nom du client que porte le titre (CONTRACT-telemetry § 1, v1.9).
 */
import type { NavItem, NavStructure } from './navigation'

export const NOM_PRODUIT = 'Pragmelis 365'
export const LIBELLE_ACCES_REFUSE = 'Accès refusé'

/** Pages de la plateforme absentes du menu. */
// Spec 161 — « Aide » entre au bas du menu : elle y est nommée par la navigation, et cette ligne ne sert plus que de
// repli (même libellé) pour une navigation absente.
const PAGES_PLATEFORME: readonly { chemin: string; libelle: string }[] = [
  { chemin: '/aide', libelle: 'Aide' },
  { chemin: '/reseller/clients', libelle: 'Fiche client' },
  { chemin: '/admin/onboarding', libelle: 'Onboarding' },
  { chemin: '/access-denied', libelle: LIBELLE_ACCES_REFUSE },
]

function couvre(chemin: string, pathname: string): boolean {
  return pathname === chemin || pathname.startsWith(`${chemin}/`)
}

/**
 * Spec 161 (D-161-07) — une entrée à onglets couvre aussi le chemin de chacun de ses onglets ; la page s'y nomme
 * « Entrée › Onglet », et le chemin rendu est celui de l'ONGLET (le changement de client reste sur la même page).
 * Les onglets passent avant l'entrée : à chemin égal (l'entrée pointe sur son premier onglet), l'onglet l'emporte.
 */
function cheminsNommes(i: NavItem): { chemin: string; libelle: string }[] {
  const onglets = (i.onglets ?? []).map(o => ({ chemin: o.path, libelle: `${i.label} › ${o.label}` }))
  return [...onglets, { chemin: i.path, libelle: i.label }]
}

/** L'entrée (de navigation, ou page de la plateforme) de plus long préfixe, ou `null`. */
export function entreeCouvrante(pathname: string, nav: NavStructure | null): { chemin: string; libelle: string } | null {
  const entrees: { chemin: string; libelle: string }[] = [
    ...(nav ? [...nav.sections.flatMap(s => s.items), ...nav.bottomItems] : []).flatMap(cheminsNommes),
    ...PAGES_PLATEFORME,
  ]
  // Tri stable : à longueur égale, l'ordre ci-dessus (onglet avant entrée) départage.
  return entrees
    .filter(e => couvre(e.chemin, pathname))
    .sort((x, y) => y.chemin.length - x.chemin.length)[0] ?? null
}

/** Le libellé de la page, ou `null` si aucune entrée ne la couvre. */
export function libellePage(pathname: string, nav: NavStructure | null): string | null {
  return entreeCouvrante(pathname, nav)?.libelle ?? null
}

/** « <Page> · <Client> · Pragmelis 365 », en omettant ce qui manque. */
export function titreOnglet(libelle: string | null, nomClient: string | null): string {
  return [libelle, nomClient, NOM_PRODUIT].filter((x): x is string => Boolean(x)).join(' · ')
}

let libelleCourant: string | null = null

export function poserLibellePageCourant(libelle: string | null): void {
  libelleCourant = libelle
}

export function libellePageCourant(): string | null {
  return libelleCourant
}
